<template>
	<div class="text-center mx-auto">
		<!-- Illustration Image -->
		<img loading="lazy" :src="imageSrc" :alt="imageAlt" class="mx-auto" />

		<!-- Heading -->
		<div class="pt-8 text-xl font-semibold mb-4">
			{{ heading }}
		</div>

		<!-- Description -->
		<div class="py-5 grid grid-cols-6 gap-4 text-base mb-4">
			<div class="col-start-2 col-span-4">
				{{ processedDescription }}
			</div>
		</div>

		<!-- Buttons -->
		<div class="flex flex-wrap justify-center">
			<button @click="$emit('scroll-to-form')" class="button m-2">
				{{ primaryButtonText }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import type { Store } from "vuex/types/index.js";
import type PersistedState from "~/domains/store/PersistedState";
import type State from "~/domains/store/State";

const store: Store<State> = useStore();

const props = defineProps({
	imageSrc: {
		type: String,
		required: true,
	},
	imageAlt: {
		type: String,
		default: "An illustration of flowers and grass.",
	},
	heading: {
		type: String,
		required: true,
	},
	description: {
		type: String,
		required: true,
	},
	primaryButtonText: {
		type: String,
		default: "Get a Free Quote",
	},
	primaryButtonLink: {
		type: String,
		default: "/",
	},
	currentPackage: {
		type: [Number, Object],
		default: null,
	},
});

const couponAmount = ref<string | null>(null); // To store the coupon amount dynamically

const processedDescription = computed(() => {
	return props.description.replace(
		"{{default offer}}",
		couponAmount.value || "an amazing offer"
	);
});

watch(
	() => store.state.persistedState,
	(newValue) => {
		loadPersistedState(newValue);
	},
	{ immediate: false, deep: true }
);

const loadPersistedState = (persistedState: PersistedState) => {
	if (!persistedState?.branchInfo?.UPPackage) return;

	const { UPPackage, IMCYear } = persistedState.branchInfo;
	const filteredData = UPPackage.find(
		(item) => item.Year === IMCYear && props.currentPackage === item.PNameID
	);

	if (filteredData) {
		const primaryOffer = filteredData["Programs and Services"].find(
			(service) => service.PrimaryOffer === true
		);
		couponAmount.value = primaryOffer?.['Programs / Services'] || null;
	}
};

onMounted(() => {
	loadPersistedState(store.state.persistedState);
});
</script>

<style scoped>
/* Add any scoped styles if needed */
</style>
